import React, { useState, useEffect, createRef } from "react";
import { withRouter } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Multiselect } from "multiselect-react-dropdown";
import moment from "moment";

import DatePicker from "react-datepicker";
import { selectUsers } from "../state/reducers/userSlice";
import {
  clearPatId,
  clearPatIdForOrder,
  updatePatId,
  getPatIdForOrders,
} from "../state/reducers/patientSlice";

import { connect, useSelector, useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import CipHeader from "../Components/CipHeader";
import orderService from "../services/new-order-service";
import cogoToast from "cogo-toast";
import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";
import Loader from "react-loader-spinner";
import patientService from "../services/patient-service";

const CreateOrder = ({ history }) => {
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  let { user } = useSelector(selectUsers);
  let { patId } = useSelector(getPatIdForOrders);

  const [error, setError] = useState(false);
  const [patientList, setPatientList] = useState([]);
  const [pat_id, setPatId] = useState([]);
  const [facilityList, setFacilitiesList] = useState([]);
  const [facility, setFacility] = useState([]);
  const [drg_name, setDrugName] = useState([]);
  const [drgList, setDrugList] = useState([]);
  const [qty, setQty] = useState("");
  const [frequencyList, setFrequencyList] = useState([]);
  const [frequency, setFrequency] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const searchTimerRef = React.useRef(null);
  const hasPrefilledRef = React.useRef(false);

  // ── Allergy state ─────────────────────────────────────────────
  const multiselectRefAllergy = createRef();
  const [allergy, setAllergy] = useState([]);
  const [defaultAllergies, setDefaultAllergies] = useState([]);
  const [allergyList, setAllergyList] = useState([]);

  const [startDate, setStartDate] = useState(
    moment(new Date(), "MM-DD-YYYY").add(1, "days").toDate()
  );
  const [duration, setDuration] = useState("");
  const [endDate, setEndDate] = useState("");
  const [doctor, setDoctor] = useState([]);
  const [doctorList, setDoctorList] = useState([]);
  const [profiled, setProfiled] = useState(false);
  const [prn, setPrn] = useState(false);

  // ── Patient search helpers ─────────────────────────────────────
  const normalizeSearch = (value = "") => {
    if (typeof value !== "string") return "";
    return value
      .replace(/,\s*/g, ", ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const reloadPatients = async () => {
    try {
      setLoading(true);
      const resp = await orderService.getPatientsList("", 100);
      const data = resp?.list || [];
      const finalArray = data.map((res) => {
        const code = res.acct_number ? ` (${res.acct_number})` : "";
        return {
          id: res.id,
          fullname: `${res.lname.toUpperCase()}, ${res.fname.toUpperCase()}${code}`,
          acct_number:res.acct_number,
          fname:res.fname,
          lname:res.lname,
          fac_id: res.fac_id,
          fac_dcode: res.fac_dcode,
        };
      });
      setPatientList(finalArray);
    } catch {
      cogoToast.error("Error retrieving patients.");
    } finally {
      setLoading(false);
    }
  };

  const getPatients = async (search) => {
    if (!search || !search.trim()) {
      await reloadPatients();
      return;
    }
    const normalizedSearch = normalizeSearch(search);
    try {
      const resp = await orderService.getPatientsList(normalizedSearch);
      const data = resp?.list || [];
      const finalArray = data.map((res) => {
        const code = res.acct_number ? ` (${res.acct_number})` : "";
        return {
          id: res.id,
          fullname: `${res.lname.toUpperCase()}, ${res.fname.toUpperCase()}${code}`,
          acct_number:res.acct_number,
          fname:res.fname,
          lname:res.lname,
          fac_id: res.fac_id,
          fac_dcode: res.fac_dcode,
        };
      });
      setPatientList([]);
      setTimeout(() => {
        setPatientList(finalArray);
      }, 0);
    } catch {
      cogoToast.error("Error retrieving patients.");
    }
  };

  // ── Allergy helpers ───────────────────────────────────────────
  const getAllergies = async (search) => {
    if (search.trim().length >= 2) {
      setAllergyList([]);
      try {
        let response = await patientService.getAllergies(search);
        setAllergyList([...response]);
      } catch (e) {
        const { hide } = cogoToast.error(
          "Error retrieving allergies. Please contact administrator.",
          {
            hideAfter: 15,
            onClick: () => { hide(); },
          }
        );
      }
    }
  };

  const getPatientAllergies = async (patientId) => {
    try {
      let response = await orderService.getPatientAllergies(patientId);
      const unique = response.filter(
        (item, idx, arr) =>
          arr.findIndex((a) => a.allergy_id === item.allergy_id) === idx
      );
      setAllergy([...unique]);
      setDefaultAllergies([...unique]);
    } catch (e) {
      const { hide } = cogoToast.error(
        "Error retrieving allergies. Please contact administrator.",
        {
          hideAfter: 15,
          onClick: () => { hide(); },
        }
      );
    }
  };
  // ─────────────────────────────────────────────────────────────

  const getDrugList = async (search) => {
    if (search.trim().length >= 2) {
      try {
        let response = await orderService.getDrugs(search);
        let dList = response?.map((item) => {
          item.dname = item.dname + " " + item.strength + " " + item.dos_id;
          return item;
        });
        setDrugList([]);
        setDrugList(dList);
      } catch (e) {
        const { hide } = cogoToast.error(
          "Error retrieving drugs. Please contact administrator.",
          {
            hideAfter: 15,
            onClick: () => { hide(); },
          }
        );
      }
    }
  };

  const getFrequencyList = async () => {
    try {
      let response = await orderService.getFrequency();
      setFrequencyList([]);
      setFrequencyList(response);
    } catch (e) {
      const { hide } = cogoToast.error(
        "Error retrieving frequency. Please contact administrator.",
        {
          hideAfter: 15,
          onClick: () => { hide(); },
        }
      );
    }
  };

  const getDoctorList = async (search) => {
    if (facility?.length === 0) {
      const { hide } = cogoToast.error("Please select facility", {
        hideAfter: 15,
        onClick: () => { hide(); },
      });
      return;
    }
    if (search.trim().length >= 0) {
      try {
        let response = await orderService.getDoctors(search, facility[0]?.id);
        let rList = response?.map((item) => {
          let mname = item.mname ? item.mname : "";
          item.fname = item.fname + " " + mname + " " + item.lname;
          return item;
        });
        setDoctorList([]);
        setDoctorList(rList);
      } catch (e) {
        const { hide } = cogoToast.error(
          "Error retrieving the list of doctors. Please contact administrator.",
          {
            hideAfter: 15,
            onClick: () => { hide(); },
          }
        );
      }
    }
  };

  // ── Effects ───────────────────────────────────────────────────
  useEffect(() => {
    if (startDate && duration)
      setEndDate(
        moment(startDate, "MM-DD-YYYY").add(duration - 1, "days").toDate()
      );
  }, [startDate, duration]);

  useEffect(() => {
    if (hasPrefilledRef.current || !patId?.length) return;

    const tryPrefill = async () => {
      // 1. Try to find the patient in the already-loaded list
      let matched = patientList.filter((p) => p.id === patId[0].id);

      if (!matched.length) {
        // 2. Patient not in current page — fetch by last name
        const rawName = patId[0].fname || patId[0].name || "";
        const lastName = rawName.split(",")[0].trim();
        if (!lastName) return;

        try {
          const resp = await orderService.getPatientsList(lastName, 100);
          const data = resp?.list || [];
          const fetched = data.map((res) => {
            const code = res.acct_number ? ` (${res.acct_number})` : "";
            return {
              id: res.id,
              fullname: `${res.lname.toUpperCase()}, ${res.fname.toUpperCase()}${code}`,
              acct_number:res.acct_number,
              fname:res.fname,
              lname:res.lname,
              fac_id: res.fac_id,
              fac_dcode: res.fac_dcode,
            };  
          });
          matched = fetched.filter((p) => p.id === patId[0].id);

          // 3. Merge fetched patients into patientList without duplicates
          if (fetched.length) {
            setPatientList((prev) => {
              const existingIds = new Set(prev.map((p) => p.id));
              const newOnes = fetched.filter((p) => !existingIds.has(p.id));
              return newOnes.length ? [...prev, ...newOnes] : prev;
            });
          }
        } catch {
          // prefill is best-effort; silent failure
        }
      }

      // 4. Prefill once the patient is found via either path
      if (matched.length) {
        hasPrefilledRef.current = true;
        setPatId(matched);
        getPatientAllergies(matched[0].id);
      }
    };

    tryPrefill();
  }, [patId, patientList]);

  useEffect(() => {
    getFacilities("");
    getFrequencyList();
    getPatients("");
    return function cleanup() {
      dispatch(clearPatIdForOrder());
    };
  }, []);

  const dispatch = useDispatch();
  const multiselectRefDoctor = createRef();
  const multiselectRefPatient = createRef();
  const multiselectRefFrequency = createRef();
  const multiselectRefDrug = createRef();
  const multiselectRefFacility = createRef();

  async function getFacilities(search) {
    try {
      setLoading(true);
      let totalFacList = await orderService.getFacilities(search);
      totalFacList.sort((a, b) => a.dname.localeCompare(b.dname));
      setFacilitiesList(totalFacList);
      setLoading(false);
      if (!hasPrefilledRef.current) {
        var userFacility = totalFacList.filter(
          (item) => item.id === user?.facility
        );
        setFacility(userFacility);
      }
    } catch (e) {
      const { hide } = cogoToast.error(
        "Error while getting facilities details. Please try after sometime.",
        {
          hideAfter: 15,
          onClick: () => { hide(); },
        }
      );
      setLoading(false);
    }
  }

  // ── Submit flow ───────────────────────────────────────────────
  const onSubmit = async () => {
    try {
      if (
        pat_id?.length === 0 ||
        facility?.length === 0 ||
        drg_name?.length === 0 ||
        qty?.length === 0 ||
        Number(qty) <= 0 ||
        frequency?.length === 0 ||
        startDate === null ||
        duration === "" ||
        Number(duration) > 365 ||
        Number(duration) < 1 ||
        doctor?.length === 0 ||
        allergy.length === 0
      ) {
        setError(true);
        return;
      }
      handleCreation();
    } catch (e) {
      const { hide } = cogoToast.error(
        "Error while submitting order details. Please try after sometime.",
        {
          hideAfter: 15,
          onClick: () => { hide(); },
        }
      );
      setLoading(false);
    }
  };

  const buildAllergyParam = () => {
    const areArraysEqual =
      JSON.stringify(allergy) === JSON.stringify(defaultAllergies);
    return areArraysEqual ? null : allergy;
  };

  const handleCreation = async () => {
    try {
      var params = {};
      params["pat_id"] = pat_id[0]?.id;
      params["fac_id"] = facility[0]?.id;
      params["drg_id"] = drg_name[0]?.id;
      params["rxf_qty_dose"] = qty;
      params["rxf_freq"] = frequency[0]?.dcode;
      params["rxf_sig"] = additionalNotes;
      params["rxf_org_date"] = moment(startDate).format("YYYY-MM-DD");
      params["rxf_exp_date"] =  endDate ? moment(endDate).format("YYYY-MM-DD") : null;
      params["rxf_days_supply"] = duration;
      params["doc_id"] = doctor[0]?.id;
      params["profile_flag"] = profiled ? "T" : "F";
      params["rxf_prn_flag"] = prn ? "T" : "F";
      params["allergies"] = buildAllergyParam();

      await orderService.createOrder(params);
      dispatch(updatePatId(pat_id));
      setLoading(false);
      await history.push("/patientDetails");
    } catch (e) {
      const { hide } = cogoToast.error(
        "Error while submitting order details. Please try after sometime.",
        {
          hideAfter: 15,
          onClick: () => { hide(); },
        }
      );
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (pat_id?.length && facilityList?.length) {
      const matched = facilityList.filter(
        (item) => item.id === pat_id[0].fac_id
               || item.dcode === pat_id[0].fac_dcode
      );
      if (matched.length) {
        setFacility(matched);
      }
    }
  }, [pat_id, facilityList]);

  const [disableNotes, setDisableNotes] = useState(false);
  const facs = [1031, 1986, 1987, 1988, 1992, 1989, 1990, 1991];
  useEffect(() => {
    if (facility?.length && facs.includes(facility[0].id)) {
      setAdditionalNotes("Release Med");
      setDisableNotes(true);
    } else {
      setDisableNotes(false);
      setAdditionalNotes("");
    }
  }, [facility]);

  return (
    <>
      <div
        className={`inline-block customtop absolute z-50 ${
          loading ? "show" : "hidden"
        }`}
      >
        <Loader
          type="Oval"
          color="#00B2A9"
          height={50}
          width={50}
          timeout={300000}
        />
      </div>
      <CipHeader />

      <div className="bg-white w-3/4 mx-auto mt-6 mb-4 rounded shadow-md">
        <div className="tableHeader flex clearboth shadow-md py-8 pl-3 h-24">
          <div className="w-full text-darkclr font-semibold leading-10">
            <span className="text-xl">
              {id ? "EDIT ORDER" : "CREATE ORDER"}
            </span>
          </div>
        </div>
        <div className="px-12 py-5">
          <ul className="text-left">
            {/* Patient + Facility */}
            <li className="inline-block text-xs leading-6 text-menu_txt w-full mb-3">
              <div className="flex">
                <span className="w-1/2 mr-16">
                  <label className="text-labelclr font-semibold text-sm tracking-wider">
                    Patient
                  </label>
                  <span className="text-xs md:text-sm text-hlred ml-1">*</span>
                  {patientList ? (
                    <Multiselect
                      options={patientList}
                      displayValue="fullname"
                      selectedValues={pat_id}
                      selectionLimit={1}
                      hidePlaceholder
                      HighlightFirstOption
                      onSearch={(value) => {
                        if (searchTimerRef.current) {
                          clearTimeout(searchTimerRef.current);
                        }
                        searchTimerRef.current = setTimeout(() => {
                          getPatients(value);
                        }, 400);
                      }}
                      caseSensitiveSearch={false}
                      onSelect={(list, item) => {
                        setPatId([item]);
                        getPatientAllergies(item.id);
                      }}
                      onRemove={async () => {
                        dispatch(clearPatIdForOrder());
                        setPatId([]);
                        setAllergy([]);
                        setDefaultAllergies([]);
                        setPatientList([]);
                        const userFacility = facilityList.filter(
                          (item) => item.id === user?.facility
                        );
                        setFacility(userFacility);
                        setTimeout(() => {
                          getPatients("");
                        }, 50);
                      }}
                    />
                  ) : (
                    ""
                  )}
                  {error && pat_id?.length === 0 && (
                    <p className="text-xs md:text-sm md:pt-1 text-hlred">
                      You need to specify patient
                    </p>
                  )}
                </span>
                <span className="w-1/2">
                  <label className="text-labelclr font-semibold text-sm tracking-wider">
                    Facility
                  </label>
                  <span className="text-xs md:text-sm text-hlred ml-1">*</span>
                  {facilityList ? (
                    <Multiselect
                      name="faciltyList"
                      displayValue="dname"
                      selectedValues={facility}
                      options={facilityList}
                      ref={multiselectRefFacility}
                      avoidHighlightFirstOption={true}
                      selectionLimit={1}
                      hidePlaceholder={true}
                      caseSensitiveSearch={true}
                      onSelect={(selectedList, selectedItem) => {
                        setFacility([selectedItem]);
                      }}
                      onSearch={(value) => {
                        getFacilities(value);
                      }}
                      onRemove={() => {
                        setFacility([]);
                      }}
                    />
                  ) : (
                    ""
                  )}
                  {error && facility?.length === 0 && (
                    <p className="text-xs md:text-sm md:pt-1 text-hlred">
                      You need to specify facility
                    </p>
                  )}
                </span>
              </div>
            </li>

            {/* Drug + Qty */}
            <li className="inline-block text-xs leading-6 text-menu_txt w-full mb-3">
              <div className="flex">
                <span className="w-1/2 mr-16">
                  <label className="text-labelclr font-semibold text-sm tracking-wider">
                    Drug Name
                  </label>
                  <span className="text-xs md:text-sm text-hlred ml-1">*</span>
                  {drgList ? (
                    <Multiselect
                      name="drgList"
                      displayValue="dname"
                      selectedValues={drg_name}
                      options={drgList}
                      ref={multiselectRefDrug}
                      avoidHighlightFirstOption={true}
                      selectionLimit={1}
                      hidePlaceholder={true}
                      caseSensitiveSearch={true}
                      onSelect={(selectedList, selectedItem) => {
                        setDrugName([selectedItem]);
                      }}
                      onSearch={(value) => {
                        getDrugList(value);
                      }}
                      onRemove={() => {
                        setDrugName([]);
                      }}
                    />
                  ) : (
                    ""
                  )}
                  {error && drg_name?.length === 0 && (
                    <p className="text-xs md:text-sm md:pt-1 text-hlred">
                      You need to specify drug name
                    </p>
                  )}
                </span>
                <span className="w-1/2">
                  <label className="text-labelclr font-semibold text-sm tracking-wider">
                    Quantity per dose
                  </label>
                  <span className="text-xs md:text-sm text-hlred ml-1">*</span>
                  <input
                    id="qty"
                    name="qty"
                    type="number"
                    placeholder="Quantity"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    className={` ${
                      error && qty === "" ? "border-red-400" : ""
                    } border focus:outline-none h-11 rounded-md pl-2 font-normal w-full text-fogotpwsd_login text-sm`}
                  />
                  {error && qty === "" && (
                    <p className="text-xs md:text-sm md:pt-1 text-hlred">
                      You need to specify quantity
                    </p>
                  )}
                  {qty?.length > 0 && Number(qty) <= 0 && (
                    <p className="text-xs md:text-sm md:pt-1 text-hlred">
                      Please enter a quantity greater than zero
                    </p>
                  )}
                </span>
              </div>
            </li>

            {/* Frequency + Sig notes */}
            <li className="inline-block text-xs leading-6 text-menu_txt w-full mb-3">
              <div className="flex">
                <span className="w-1/2 mr-16">
                  <label className="text-labelclr font-semibold text-sm tracking-wider">
                    Frequency
                  </label>
                  <span className="text-xs md:text-sm text-hlred ml-1">*</span>
                  {frequencyList ? (
                    <Multiselect
                      name="frequencyList"
                      displayValue="dcode"
                      selectedValues={frequency}
                      options={frequencyList}
                      ref={multiselectRefFrequency}
                      avoidHighlightFirstOption={true}
                      selectionLimit={1}
                      hidePlaceholder={true}
                      onSelect={(selectedList, selectedItem) => {
                        setFrequency([selectedItem]);
                      }}
                      onRemove={() => {
                        setFrequency([]);
                      }}
                    />
                  ) : (
                    ""
                  )}
                  {error && frequency?.length === 0 && (
                    <p className="text-xs md:text-sm md:pt-1 text-hlred">
                      You need to specify frequency
                    </p>
                  )}
                </span>
                <span className="w-1/2">
                  <label className="text-labelclr font-semibold text-sm tracking-wider">
                    Additional sig notes
                  </label>
                  <input
                    id="additionalNotes"
                    name="additionalNotes"
                    type="text"
                    disabled={disableNotes}
                    placeholder="Additional sig notes"
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    className={`border focus:outline-none h-11 rounded-md pl-2 font-normal w-full text-fogotpwsd_login text-sm`}
                  />
                </span>
              </div>
            </li>

            {/* Start Date + Duration */}
            <li className="inline-block text-xs leading-6 w-full mb-4">
              <div className="flex">
                <span className="w-1/2">
                  <label className="text-labelclr font-semibold text-sm tracking-wider">
                    Start Date
                  </label>
                  <span className="text-xs md:text-sm text-hlred ml-1 mr-8">
                    *
                  </span>
                  <DatePicker
                    placeholderText="MM/DD/YYYY"
                    selected={startDate}
                    onChange={(dob) => {
                      setStartDate(dob);
                    }}
                    className="border focus:outline-none h-11 rounded-md pl-2 font-normal w-full text-fogotpwsd_login text-sm"
                  />
                  {error && startDate === null && (
                    <p className="text-xs md:text-sm md:pt-1 text-hlred table">
                      You must specify start date
                    </p>
                  )}
                </span>
                <span className="w-1/2 pl-8">
                  <label className="text-labelclr font-semibold text-sm tracking-wider">
                    Length of the order/Duration
                  </label>
                  <span className="text-xs md:text-sm text-hlred ml-1">*</span>
                  <input
                    id="duration"
                    name="duration"
                    type="number"
                    max={365}
                    value={duration}
                    placeholder="Length of the order/Duration"
                    onChange={(e) => setDuration(e.target.value)}
                    className={` ${
                      error && duration === "" ? "border-red-400" : ""
                    } border focus:outline-none h-11 rounded-md pl-2 font-normal w-full text-fogotpwsd_login text-sm`}
                  />
                  {error && duration === "" && (
                    <p className="text-xs md:text-sm md:pt-1 text-hlred table">
                      You must specify duration
                    </p>
                  )}
                  {duration?.length > 0 &&
                    (Number(duration) > 365 || Number(duration) < 1) && (
                      <p className="text-xs md:text-sm md:pt-1 text-hlred table">
                        Enter a duration (1-365 days).
                      </p>
                    )}
                </span>
              </div>
            </li>

            {/* End Date + Doctor */}
            <li className="inline-block text-xs leading-6 w-full mb-4">
              <div className="flex">
                <span className="w-1/2">
                  <div>
                    <label className="text-labelclr font-semibold text-sm tracking-wider">
                      End Date
                    </label>
                  </div>
                  <DatePicker
                    placeholderText="MM/DD/YYYY"
                    selected={endDate}
                    disabled={true}
                    onChange={(dob) => {
                      setEndDate(dob);
                    }}
                    className="border focus:outline-none h-11 rounded-md pl-2 font-normal w-full text-fogotpwsd_login text-sm"
                  />
                </span>
                <span className="w-1/2 pl-8">
                  <label className="text-labelclr font-semibold text-sm tracking-wider">
                    Doctor
                  </label>
                  <span className="text-xs md:text-sm text-hlred ml-1">*</span>
                  {doctorList ? (
                    <Multiselect
                      name="doctorList"
                      displayValue="fname"
                      selectedValues={doctor}
                      options={doctorList}
                      ref={multiselectRefDoctor}
                      avoidHighlightFirstOption={true}
                      selectionLimit={1}
                      hidePlaceholder={true}
                      caseSensitiveSearch={true}
                      onSelect={(selectedList, selectedItem) => {
                        setDoctor([selectedItem]);
                      }}
                      onSearch={(value) => {
                        getDoctorList(value);
                      }}
                      onRemove={() => {
                        setDoctor([]);
                      }}
                    />
                  ) : (
                    ""
                  )}
                  {error && doctor?.length === 0 && (
                    <p className="text-xs md:text-sm md:pt-1 text-hlred table">
                      You must specify doctor
                    </p>
                  )}
                </span>
              </div>
            </li>

            {/* Allergies */}
            <li className="inline-block text-xs leading-6 text-menu_txt w-full mb-3">
              <div className="flex">
                <span className="w-1/2">
                  <label className="text-labelclr font-semibold text-sm tracking-wider">
                    Allergies
                  </label>
                  <span className="text-xs md:text-sm text-hlred ml-1">*</span>
                  {allergyList ? (
                    <Multiselect
                      name="allergyList"
                      displayValue="text"
                      selectedValues={allergy}
                      options={allergyList}
                      ref={multiselectRefAllergy}
                      avoidHighlightFirstOption={true}
                      hidePlaceholder={true}
                      onSelect={(selectedList) => {
                        setAllergy(selectedList);
                      }}
                      onSearch={(value) => {
                        getAllergies(value);
                      }}
                      onRemove={(selectedList) => {
                        setAllergy(selectedList);
                      }}
                    />
                  ) : (
                    ""
                  )}
                  {error && allergy.length === 0 && (
                    <p className="text-xs md:text-sm md:pt-1 text-hlred table">
                      You must select an Allergy
                    </p>
                  )}
                </span>
                <span className="w-1/2 pl-8">
                  <button
                    type="button"
                    className="text-white w-32 h-12 py-2 mt-6 bg-header_bg rounded text-xs font-semibold capitalize"
                    onClick={() => {
                      setAllergy([
                        {
                          allergy_id: "276574",
                          text: "No Known Allergies",
                          type: "NULL",
                        },
                      ]);
                    }}
                  >
                    No Known Allergies
                  </button>
                </span>
              </div>
            </li>

            {/* Checkboxes */}
            <li>
              <span className="">
                <input
                  type="checkbox"
                  id="prn"
                  name="prn"
                  value={prn}
                  defaultChecked={prn ? true : false}
                  onClick={(e) => setPrn(e.target.checked)}
                />
                <label> PRN</label>
              </span>
            </li>
            <li>
              <span className="">
                <input
                  type="checkbox"
                  id="profiler"
                  name="profiler"
                  value={profiled}
                  defaultChecked={profiled ? true : false}
                  onClick={(e) => setProfiled(e.target.checked)}
                />
                <label> Profile</label>{" "}
                <span className="text-xs text-hlred font-bold">
                  *If "Profile" is selected, the medication order will be
                  profiled for MAR purposes ONLY, and not eligible to be filled.*
                </span>
              </span>
            </li>

            <li>
              <button
                type="submit"
                className="text-white w-full h-8 px-6 mt-2 sm:mt-5 bg-header_bg rounded text-xs font-semibold capitalize"
                onClick={() => onSubmit()}
              >
                SUBMIT
              </button>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default withRouter(
  connect(
    () => ({}),
    (dispatch) => {
      return bindActionCreators({}, dispatch);
    }
  )(CreateOrder)
);